(() => {
  const KEY = 'ditch:saved';
  const inventory = new Map();
  const prioritizeItems = async (items) => {
    const current = await Promise.all(items.map(async (item) => {
      if (!inventory.has(item.handle)) {
        const root = window.Shopify?.routes?.root || '/';
        inventory.set(item.handle, fetch(`${root}products/${encodeURIComponent(item.handle)}.js`)
          .then((response) => response.ok ? response.json() : null)
          .catch(() => null));
      }
      const product = await inventory.get(item.handle);
      return { ...item, available: product ? product.available : item.available };
    }));
    return [...current.filter((item) => item.available !== false), ...current.filter((item) => item.available === false)];
  };

  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  };

  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch {}
    document.dispatchEvent(new CustomEvent('bookmarks:change'));
  };

  const has = (handle) => read().some((item) => item.handle === handle);

  const toggle = (item) => {
    const list = read();
    const index = list.findIndex((saved) => saved.handle === item.handle);
    if (index >= 0) {
      list.splice(index, 1);
    } else {
      list.push(item);
    }
    write(list);
  };

  const buildCard = (template, item) => {
    const card = template.content.cloneNode(true);
    card.querySelectorAll('[data-card-link]').forEach((link) => {
      link.href = item.url;
    });
    const image = card.querySelector('[data-card-image]');
    if (item.image) {
      image.src = item.image;
      image.alt = item.title;
    } else {
      image.remove();
    }
    card.querySelector('[data-card-name]').textContent = item.title;
    card.querySelector('[data-card-price]').textContent = item.price || '';
    const button = card.querySelector('button');
    Object.assign(button.dataset, {
      handle: item.handle,
      url: item.url,
      title: item.title,
      price: item.price || '',
      image: item.image || '',
    });
    return card;
  };

  window.addEventListener('storage', (event) => {
    if (event.key === KEY) document.dispatchEvent(new CustomEvent('bookmarks:change'));
  });

  if (!customElements.get('bookmark-toggle')) {
    customElements.define(
      'bookmark-toggle',
      class extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          if (!this.button) return;
          this.onChange = () => this.sync();
          document.addEventListener('bookmarks:change', this.onChange);
          this.button.addEventListener('click', (event) => {
            event.preventDefault();
            const data = this.button.dataset;
            toggle({
              handle: data.handle,
              url: data.url,
              title: data.title,
              price: data.price,
              image: data.image,
            });
          });
          this.sync();
        }
        disconnectedCallback() {
          document.removeEventListener('bookmarks:change', this.onChange);
        }
        sync() {
          if (!this.button) return;
          const saved = has(this.button.dataset.handle);
          this.button.classList.toggle('is-saved', saved);
          this.button.setAttribute('aria-pressed', String(saved));
          const label = saved ? this.button.dataset.labelRemove : this.button.dataset.labelSave;
          if (label) this.button.setAttribute('aria-label', label);
        }
      }
    );
  }

  if (!customElements.get('bookmark-count')) {
    customElements.define(
      'bookmark-count',
      class extends HTMLElement {
        connectedCallback() {
          this.onChange = () => this.sync();
          document.addEventListener('bookmarks:change', this.onChange);
          this.sync();
        }
        disconnectedCallback() {
          document.removeEventListener('bookmarks:change', this.onChange);
        }
        sync() {
          const count = read().length;
          this.textContent = count > 0 ? String(count) : '';
        }
      }
    );
  }

  if (!customElements.get('bookmark-grid')) {
    customElements.define(
      'bookmark-grid',
      class extends HTMLElement {
        connectedCallback() {
          this.grid = this.querySelector('[data-saved-grid]');
          this.empty = this.querySelector('[data-saved-empty]');
          this.template = this.querySelector('template');
          if (!this.grid || !this.empty || !this.template) return;
          this.onChange = () => this.render();
          document.addEventListener('bookmarks:change', this.onChange);
          this.render();
        }
        disconnectedCallback() {
          document.removeEventListener('bookmarks:change', this.onChange);
        }
        async render() {
          const revision = this.renderRevision = (this.renderRevision || 0) + 1;
          const items = read();
          this.grid.hidden = items.length === 0;
          this.empty.hidden = items.length > 0;
          const ordered = await prioritizeItems(items);
          if (!this.isConnected || revision !== this.renderRevision) return;
          this.grid.replaceChildren(...ordered.map((item) => buildCard(this.template, item)));
        }
      }
    );
  }

  if (!customElements.get('bookmark-row')) {
    customElements.define(
      'bookmark-row',
      class extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-saved-track]');
          this.template = this.querySelector('template');
          if (!this.track || !this.template) return;
          const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
          this.querySelectorAll('[data-scroll]').forEach((button) => {
            button.addEventListener('click', () => {
              this.track.scrollBy({ left: Number(button.dataset.scroll) * this.track.clientWidth, behavior });
            });
          });
          this.onChange = () => this.render();
          document.addEventListener('bookmarks:change', this.onChange);
          this.render();
        }
        disconnectedCallback() {
          document.removeEventListener('bookmarks:change', this.onChange);
        }
        async render() {
          const revision = this.renderRevision = (this.renderRevision || 0) + 1;
          const items = read().filter((item) => item.handle !== this.dataset.exclude);
          this.hidden = items.length === 0;
          const ordered = await prioritizeItems(items);
          if (!this.isConnected || revision !== this.renderRevision) return;
          this.track.replaceChildren(...ordered.map((item) => buildCard(this.template, item)));
        }
      }
    );
  }
})();
